import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useApp } from "../lib/store";
import { WindingPicker } from "./WindingPicker";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { CommodityGroupSelect } from "./srm/CommodityGroupPicker";

export const ARTICLE_FAMILIES = ["Roll/PS Labels", "Shrink Sleeves", "Flexible Packaging", "Folding Cartons", "Other"];
export const EMPTY_ARTICLE = {
  name: "", account_id: "", product_family: "Roll/PS Labels", material: "", finishing: "",
  width_mm: "", height_mm: "", colors: "", winding_direction: 1, core_size_mm: 76, labels_per_roll: 2000,
  unit: "1000 pcs", price_per_1000: "", min_order_qty: 10000, status: "Active", notes: "",
};

// Load the accounts list once (shared by both article edit dialogs).
export function useAccountsList() {
  const [accounts, setAccounts] = useState([]);
  useEffect(() => {
    api.get("/accounts?limit=500")
      .then((r) => setAccounts(r.data.sort((a, b) => a.name.localeCompare(b.name))))
      .catch(() => {});
  }, []);
  return accounts;
}

// Single source of truth for the article master edit fields. Rendered by both
// the standalone /articles dialog and the account-scoped article tab dialog.
export function ArticleFormFields({ editing, set, accounts }) {
  const { t } = useApp();
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <Label className="text-xs">{t("art_name")} *</Label>
          <Input data-testid="article-name-input" className="mt-1" value={editing.name || ""} onChange={(e) => set("name", e.target.value)} />
        </div>
        <div>
          <Label className="text-xs">{t("ord_customer")}</Label>
          <select data-testid="article-account-select" className="w-full h-9 rounded-md border border-gray-200 bg-white px-2 text-sm mt-1"
            value={editing.account_id || ""} onChange={(e) => set("account_id", e.target.value)}>
            <option value="">–</option>
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs">{t("art_family")}</Label>
          <select className="w-full h-9 rounded-md border border-gray-200 bg-white px-2 text-sm mt-1"
            value={editing.product_family || ""} onChange={(e) => set("product_family", e.target.value)}>
            {ARTICLE_FAMILIES.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs">{t("art_material")}</Label>
          <Input className="mt-1" value={editing.material || ""} onChange={(e) => set("material", e.target.value)} />
        </div>
        <div>
          <Label className="text-xs">Warengruppe</Label>
          <CommodityGroupSelect testId="article-commodity-group-select" value={editing.commodity_group_id || ""} onChange={(v) => set("commodity_group_id", v)} />
        </div>
        <div>
          <Label className="text-xs">{t("art_finishing")}</Label>
          <Input className="mt-1" value={editing.finishing || ""} onChange={(e) => set("finishing", e.target.value)} />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs">B (mm)</Label>
            <Input type="number" className="mt-1" value={editing.width_mm || ""} onChange={(e) => set("width_mm", e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">H (mm)</Label>
            <Input type="number" className="mt-1" value={editing.height_mm || ""} onChange={(e) => set("height_mm", e.target.value)} />
          </div>
        </div>
        <div>
          <Label className="text-xs">{t("art_colors")}</Label>
          <Input className="mt-1" placeholder="z. B. 4c" value={editing.colors || ""} onChange={(e) => set("colors", e.target.value)} />
        </div>
        <div>
          <Label className="text-xs">{t("art_core")}</Label>
          <Input type="number" className="mt-1" value={editing.core_size_mm || ""} onChange={(e) => set("core_size_mm", e.target.value)} />
        </div>
        <div>
          <Label className="text-xs">{t("art_lpr")}</Label>
          <Input type="number" className="mt-1" value={editing.labels_per_roll || ""} onChange={(e) => set("labels_per_roll", e.target.value)} />
        </div>
        <div>
          <Label className="text-xs">{t("art_price")} (€)</Label>
          <Input type="number" step="0.01" data-testid="article-price-input" className="mt-1" value={editing.price_per_1000 || ""} onChange={(e) => set("price_per_1000", e.target.value)} />
        </div>
        <div>
          <Label className="text-xs">{t("art_moq")}</Label>
          <Input type="number" className="mt-1" value={editing.min_order_qty || ""} onChange={(e) => set("min_order_qty", e.target.value)} />
        </div>
        <div>
          <Label className="text-xs">{t("status")}</Label>
          <select className="w-full h-9 rounded-md border border-gray-200 bg-white px-2 text-sm mt-1"
            value={editing.status || "Active"} onChange={(e) => set("status", e.target.value)}>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>
      </div>
      <div>
        <Label className="text-xs mb-1.5 block">{t("art_winding")}</Label>
        <WindingPicker value={editing.winding_direction} onChange={(d) => set("winding_direction", d)} />
      </div>
    </div>
  );
}
